import { MethodMarks } from './method-marks';

/**
 * Anything that can wrap a target and hand back something of the same shape.
 *
 * Satisfied by every {@link ProxyDecorator} and by {@link ProxyPipeline}, which is
 * what lets a pipeline hold either — so a composed pipeline can itself be a step in
 * another one.
 */
export interface Decorates {
  decorate<T extends object>(target: T): T;
}

/**
 * Base class for Proxy-based decorators, applying its concern to **every** method.
 *
 * Extend this only for a concern that would be wrong to make opt-in — one where a method
 * nobody remembered to mark is a defect rather than a gap. Error translation is the
 * example: the port promises domain errors, so a method that skipped translation breaks
 * its contract instead of merely going unobserved.
 *
 * For everything else extend {@link MarkedProxyDecorator}, so the methods a concern
 * touches are declared on the class rather than inferred from what a composition root
 * happened to wrap.
 *
 * A decorator holds only its collaborators, never a target — the target is supplied
 * to {@link ProxyDecorator.decorate}. One instance can therefore decorate any number
 * of targets, so a composition root configures the concern once and applies it
 * wherever it is needed.
 *
 * @example
 * class CallCounter extends ProxyDecorator {
 *   constructor(private readonly counts: Map<string, number>) { super(); }
 *
 *   protected wrap(method: (...args: unknown[]) => unknown, methodName: string) {
 *     return (...args: unknown[]) => {
 *       this.counts.set(methodName, (this.counts.get(methodName) ?? 0) + 1);
 *       return method(...args);
 *     };
 *   }
 * }
 *
 * const counting = new CallCounter(counts);
 * const repo = counting.decorate(new MyRepository());
 * const other = counting.decorate(new OtherRepository());   // same instance, another target
 */
export abstract class ProxyDecorator implements Decorates {
  /**
   * Returns the replacement for `method`.
   *
   * `target` is last so an implementation that needs neither it nor `methodName` can
   * declare `wrap(method)` — TypeScript permits implementing with fewer parameters.
   *
   * @param method the target's method, already bound to the target
   * @param methodName the property name the method was read from
   * @param target the instance being decorated
   */
  protected abstract wrap(
    method: (...args: unknown[]) => unknown,
    methodName: string,
    target: object
  ): (...args: unknown[]) => unknown;

  public decorate<T extends object>(target: T): T {
    return new Proxy(target, {
      get: (obj, prop, receiver) => {
        const original = Reflect.get(obj, prop, receiver);
        if (typeof original !== 'function') return original;

        // `constructor` is function-valued but is never a call to intercept, and
        // wrapping it hands back an anonymous function — which silently breaks anything
        // reading `target.constructor.name`, such as CallLogger's class-name prefix, as
        // soon as one decorator wraps another.
        if (prop === 'constructor') return original;

        return this.wrap(original.bind(obj), String(prop), target);
      },
    });
  }
}

/**
 * A {@link ProxyDecorator} that acts only on the methods carrying its own marker.
 *
 * The base for every opt-in concern — logging, metrics, retrying, rate limiting. Each
 * owns a {@link MethodMarks}, and a method is affected only if it is marked; unmarked
 * methods, property reads and the constructor pass through untouched. Opting in is
 * therefore a decision recorded next to the method, which also means the decorator can be
 * applied broadly without anyone having to reason about what it will catch.
 *
 * The mark's payload is resolved here and handed to {@link MarkedProxyDecorator.wrapMarked},
 * so a subclass never looks marks up itself.
 *
 * The corollary is that an unmarked class gets nothing: a target carrying no marks for a
 * concern comes back effectively unchanged, so a missing mark reads as silence rather
 * than as an error. Worth remembering when a newly added method logs nothing.
 *
 * @template TOptions the per-method payload its marker carries; `void` for a bare gate
 *
 * @example
 * const marks = new MethodMarks<CacheOptions>('app:cachedMethods');
 *
 * export function cached(options?: CacheOptions): MethodDecorator {
 *   return (prototype, propertyKey) => marks.add(prototype, propertyKey, options);
 * }
 *
 * class Cache extends MarkedProxyDecorator<CacheOptions> {
 *   constructor(private readonly store: Map<string, unknown>) { super(marks); }
 *
 *   protected wrapMarked(method, methodName, options) {
 *     return (...args) => { ... };
 *   }
 * }
 */
export abstract class MarkedProxyDecorator<TOptions = void> extends ProxyDecorator {
  protected constructor(private readonly marks: MethodMarks<TOptions>) {
    super();
  }

  /**
   * Applies the gate, then delegates. Implement {@link MarkedProxyDecorator.wrapMarked}
   * rather than overriding this — doing so would remove the gate that is the point of
   * this class.
   */
  protected wrap(
    method: (...args: unknown[]) => unknown,
    methodName: string,
    target: object
  ): (...args: unknown[]) => unknown {
    const marked = this.marks.of(target);

    if (!marked.has(methodName)) return method;

    return this.wrapMarked(method, methodName, marked.get(methodName), target);
  }

  /**
   * Returns the replacement for `method`. Called only for marked methods.
   *
   * Parameters are ordered by how often they are needed, and TypeScript permits
   * implementing with fewer, so a decorator that only needs the method can declare
   * `wrapMarked(method)`.
   *
   * @param method the target's method, already bound to the target
   * @param methodName the property name the method was read from
   * @param options the payload on this method's mark, `undefined` for a bare mark
   * @param target the instance being decorated
   */
  protected abstract wrapMarked(
    method: (...args: unknown[]) => unknown,
    methodName: string,
    options: TOptions | undefined,
    target: object
  ): (...args: unknown[]) => unknown;
}
